import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Share2, MousePointerClick } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const PLATFORM_COLORS = {
  twitter:   'bg-black text-white',
  x:         'bg-black text-white',
  facebook:  'bg-[#1877F2] text-white',
  linkedin:  'bg-[#0A66C2] text-white',
  threads:   'bg-foreground text-background',
  instagram: 'bg-gradient-to-br from-[#fd5949] via-[#d6249f] to-[#285AEB] text-white',
  pinterest: 'bg-[#E60023] text-white',
  reddit:    'bg-[#FF4500] text-white',
  tiktok:    'bg-black text-white',
  youtube:   'bg-[#FF0000] text-white',
  whatsapp:  'bg-[#25D366] text-white',
  telegram:  'bg-[#26A5E4] text-white',
  bluesky:   'bg-[#0085FF] text-white',
  google:    'bg-[#4285F4] text-white',
  bing:      'bg-[#008373] text-white',
  direct:    'bg-muted text-foreground',
  other:     'bg-muted text-foreground',
  copy:      'bg-muted text-foreground',
  native:    'bg-[#00E5CC] text-[#0d1f2d]',
};

export const ShareStatsCard = () => {
  const [data, setData] = useState({ top_posts: [], by_platform: {} });
  const [clicks, setClicks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/admin/share-stats?limit=10').then(({ data, error }) => {
      if (!error && data) setData(data);
      setLoading(false);
    });
    apiFetch('/api/admin/share-clicks?limit=10').then(({ data, error }) => {
      if (!error && data) setClicks(data);
    });
  }, []);

  const sortedPlatforms = useMemo(
    () => Object.entries(data.by_platform).sort((a, b) => b[1] - a[1]),
    [data.by_platform],
  );

  const postsWithSortedPlatforms = useMemo(
    () => data.top_posts.map((post) => ({
      ...post,
      sorted_platforms: Object.entries(post.by_platform).sort((a, b) => b[1] - a[1]),
    })),
    [data.top_posts],
  );

  const sortedClickPlatforms = useMemo(
    () => Object.entries(clicks?.by_platform || {}).sort((a, b) => b[1] - a[1]),
    [clicks],
  );

  return (
    <Card className="mb-8" data-testid="share-stats-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Share2 className="h-5 w-5 text-[#00A896]" /> Blog share stats
        </CardTitle>
        <CardDescription>
          Inbound: readers arriving via share links (UTM-tagged, by platform).
          Outbound: where readers tap "Share" on the site.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* ── Inbound clicks (share-link traffic by platform) ── */}
        <div className="mb-5" data-testid="share-clicks-section">
          <p className="text-sm font-semibold flex items-center gap-1.5 mb-2">
            <MousePointerClick className="h-4 w-4 text-[#7C3AED]" />
            Readers arriving from share links
            {clicks && <Badge variant="outline" className="ml-1">{clicks.total} total</Badge>}
          </p>
          {!clicks || clicks.total === 0 ? (
            <p className="text-xs text-muted-foreground">
              No inbound clicks logged yet — once readers click your blog links on
              social media, platform totals show up here.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {sortedClickPlatforms.map(([p, c]) => (
                  <Badge
                    key={p}
                    className={`${PLATFORM_COLORS[p] || 'bg-muted text-foreground'} font-medium`}
                    data-testid={`share-clicks-platform-${p}`}
                  >
                    {p}: {c}
                  </Badge>
                ))}
              </div>
              <div className="space-y-1">
                {clicks.top_posts.map((tp) => (
                  <div
                    key={tp.slug}
                    className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1"
                    data-testid={`share-clicks-post-${tp.slug}`}
                  >
                    <span className="truncate">{tp.title || tp.slug}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{tp.clicks} clicks</Badge>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="border-b mb-4" />

        {/* ── Outbound share-button taps ── */}
        <p className="text-sm font-semibold mb-2">Outbound share-button taps</p>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : Object.keys(data.by_platform).length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No share events yet — once readers tap a share button, totals will show up here.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {sortedPlatforms.map(([p, c]) => (
                <Badge
                  key={p}
                  className={`${PLATFORM_COLORS[p] || 'bg-muted'} font-medium`}
                >
                  {p}: {c}
                </Badge>
              ))}
            </div>

            <div className="space-y-2">
              {postsWithSortedPlatforms.map((post) => (
                <div
                  key={post.slug}
                  className="border rounded p-2 hover:bg-muted/30 text-sm"
                  data-testid={`share-stats-post-${post.slug}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{post.title || post.slug}</span>
                    <Badge variant="outline">{post.total} total</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {post.sorted_platforms.map(([p, c]) => (
                      <span
                        key={p}
                        className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/40"
                      >
                        {p}:{c}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ShareStatsCard;
